/*
* Cascading variant availability (snippets/variant-availability.liquid)
*
* Story's SelloutVariants pass (assets/theme.js) judges every option row against
* the whole form. Two consequences on a product whose variant matrix is
* incomplete, e.g. Air Mesh Instant Tri Scarf, where Black exists in Snug Fit
* but not in Standard Fit:
*
*   - "does not exist" and "sold out" get the same slashed swatch, so a colour
*     missing from the selected fit looks like one that is merely out of stock
*   - a valid row-1 value is struck through because it clashes with the row-2
*     selection: Snug Fit goes struck through whenever Coffee is selected
*
* This layers on top without touching the compiled bundle:
*
*   - row i is judged only against the selections in rows 0..i-1
*   - a combination that does not exist is hidden, never in row 0
*   - a combination that exists but has no stock keeps Story's sold-out styling
*   - changing a row repairs the rows below it to a combination that exists
*
* Ordering is free: Story binds its listeners directly on the option inputs and
* ours is delegated on document, so bubbling always runs ours last.
*/

(function (root) {
  'use strict';

  /* --- Pure core -------------------------------------------------------
     No DOM. Unit-tested in test/variant-availability.test.cjs. */

  /* State of one option value, given the selections in the rows above it.
     Rows below rowIndex are deliberately ignored - that is the whole fix. */
  function computeState(variants, selections, rowIndex, value) {
    var exists = false;

    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options;
      if (options[rowIndex] !== value) continue;

      var matchesRowsAbove = true;
      for (var k = 0; k < rowIndex; k++) {
        if (options[k] !== selections[k]) {
          matchesRowsAbove = false;
          break;
        }
      }
      if (!matchesRowsAbove) continue;

      exists = true;
      if (variants[i].available) return 'available';
    }

    return exists ? 'sold-out' : 'missing';
  }

  /* The value to fall back to when the held one no longer exists: first in
     stock, otherwise first that exists at all, otherwise nothing. */
  function firstValidValue(variants, selections, rowIndex, values) {
    var fallback = null;

    for (var i = 0; i < values.length; i++) {
      var state = computeState(variants, selections, rowIndex, values[i]);
      if (state === 'available') return values[i];
      if (state === 'sold-out' && fallback === null) fallback = values[i];
    }

    return fallback;
  }

  /* What a value should LOOK like, which is not the same question as whether it
     exists (computeState).

     Story's original rule - judge the exact combination the customer would get -
     is right whenever that combination exists, and it is what tells a shopper
     "Plum has no 3XL". It only misleads when the combination does not exist at
     all, because then it strikes through a value that is still perfectly
     buyable: Snug Fit goes struck through merely because Coffee is selected.

     So: use the exact combination when there is one, and fall back to judging
     against the rows above only when there is not. */
  function displayState(variants, selections, rowIndex, value) {
    for (var i = 0; i < variants.length; i++) {
      var options = variants[i].options;
      if (options[rowIndex] !== value) continue;

      var exact = true;
      for (var k = 0; k < selections.length; k++) {
        if (k !== rowIndex && options[k] !== selections[k]) {
          exact = false;
          break;
        }
      }

      if (exact) return variants[i].available ? 'available' : 'sold-out';
    }

    return computeState(variants, selections, rowIndex, value);
  }

  var api = {
    computeState: computeState,
    displayState: displayState,
    firstValidValue: firstValidValue
  };

  /* Exported for the Node test runner; harmless in a browser. */
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  /* Node stops here - everything below needs a document. */
  if (typeof document === 'undefined') return;

  /* Rendered once per product section, but the snippet may end up on a page
     twice; a second execution must not double-bind. */
  if (root.GlowcoVariantAvailability) return;
  root.GlowcoVariantAvailability = api;

  /* --- DOM layer -------------------------------------------------------- */

  var HIDDEN_CLASS = 'variant-option-hidden';

  function productOf(form) {
    if (form.glowcoProduct !== undefined) return form.glowcoProduct;

    var node = form.querySelector('[data-product-json]');
    try {
      form.glowcoProduct = node ? JSON.parse(node.innerHTML) : null;
    } catch (error) {
      form.glowcoProduct = null;
    }
    return form.glowcoProduct;
  }

  /* Rows in option order; data-option-position is 1-based. */
  function rowsOf(form) {
    return Array.prototype.slice
      .call(form.querySelectorAll('[data-option-position]'))
      .sort(function (a, b) {
        return positionOf(a) - positionOf(b);
      });
  }

  function positionOf(row) {
    return parseInt(row.getAttribute('data-option-position'), 10);
  }

  /* The clickable values in a row. Excludes the dropdown's hidden input, which
     also carries an options[] name but is not something a customer picks. */
  function choicesOf(row) {
    return Array.prototype.slice.call(
      row.querySelectorAll('input[type="radio"][name^="options"], [data-popout-option]')
    );
  }

  function valueOf(el) {
    return el.value || el.getAttribute('data-value');
  }

  function selectedValueOf(row) {
    var checked = row.querySelector('input[type="radio"][name^="options"]:checked');
    if (checked) return checked.value;

    var popout = row.querySelector('[data-popout-input]');
    return popout ? popout.value : null;
  }

  function wrapperOf(el) {
    return el.closest('radio-swatch, .radio__button, .select-popout__item') || el;
  }

  /* Recompute every row of one form and apply the result. Runs after Story's
     own pass, so it overwrites the classes Story just set. */
  function update(form) {
    var product = productOf(form);
    if (!product || !product.variants) return;

    var rows = rowsOf(form);
    var selections = rows.map(selectedValueOf);

    rows.forEach(function (row, index) {
      choicesOf(row).forEach(function (el) {
        var state = displayState(product.variants, selections, index, valueOf(el));

        /* Row 0 is never hidden: hiding the fit a customer is not currently on
           would leave them no way back to it. */
        var hide = state === 'missing' && index > 0;

        wrapperOf(el).classList.toggle(HIDDEN_CLASS, hide);
        el.classList.toggle('unavailable', state === 'missing' && !hide);
        el.classList.toggle('sold-out', state === 'sold-out');
      });
    });
  }

  /* Set a row to a value the way a customer would, so Story's own listeners run
     and the price, image, inventory count and sticky bar all follow. */
  function select(el) {
    if (el.tagName === 'INPUT') {
      el.checked = true;
      el.dispatchEvent(new Event('change', {bubbles: true}));
    } else {
      el.click();
    }
  }

  /* After a change, walk the rows below it. Any row whose held value no longer
     exists moves to the first value that does, preferring one in stock.
     Selections are updated as we go so each row is judged against the repaired
     rows above it, not the stale ones. */
  function repair(form) {
    var product = productOf(form);
    if (!product || !product.variants) return;

    var rows = rowsOf(form);
    var selections = rows.map(selectedValueOf);

    for (var index = 1; index < rows.length; index++) {
      if (computeState(product.variants, selections, index, selections[index]) !== 'missing') {
        continue;
      }

      var choices = choicesOf(rows[index]);
      var values = choices.map(valueOf);
      var pick = firstValidValue(product.variants, selections, index, values);
      if (pick === null) continue;

      selections[index] = pick;
      select(choices[values.indexOf(pick)]);
    }
  }

  function updateAll() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-product-form]'),
      update
    );
  }

  /* Delegated on document so it also covers forms injected later (upsell
     modals), and so bubbling puts it after Story's per-input listeners.

     select() dispatches a change of its own, which lands back here; the flag
     stops that from recursing. The repaired form is updated once at the end. */
  var repairing = false;

  document.addEventListener('change', function (event) {
    if (repairing) return;

    var el = event.target;
    if (!el.name || el.name.indexOf('options[') !== 0) return;

    var form = el.closest('[data-product-form]');
    if (!form) return;

    repairing = true;
    try {
      repair(form);
    } finally {
      repairing = false;
    }

    update(form);
  });

  document.addEventListener('shopify:section:load', updateAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateAll);
  } else {
    updateAll();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
